import React from 'react';
import { compose } from 'redux';
import { RouteName, RoutePath, ScreenName } from '@helpcenter/constants';
import EditCustomPageResource from '@helpcenter/custom-page-resources/src/edit-custom-page';
import { ViewCustomPageResource } from '@helpcenter/custom-page-resources/src/view-custom-page/resources';
import { isIncreasedFlexibilityEnabled } from '@helpcenter/feature-flags';
import { useIntl } from 'react-intl';
import type { Route } from 'react-resource-router';
import { withOnlyJiraAdmin } from 'view/common/with-only-jira-admin';
import { withOnlyLoggedIn } from 'view/common/with-only-logged-in';
import { SplashLayoutRoute } from 'view/layout';
import { PrefetchBrandingModel } from 'view/layout/prefetch-branding-model';
import { authPageMessage, callToActionUrlText } from 'view/portal-reorder-experience';
import { createRouteWithLayout } from '@atlassian/help-center-common-util/resource-router';
import { EditCustomPage, ViewCustomPage } from '@atlassian/help-center-custom-pages';
import { brandingResource } from '../advanced-help-center/resources/multi-help-centers';

export const getViewCustomPageRoute = (
	name: string = RouteName.VIEW_CUSTOM_PAGE,
	path: string = RoutePath.VIEW_CUSTOM_PAGE,
) => {
	const viewCustomPageSSRResources = [brandingResource, ViewCustomPageResource];
	return createRouteWithLayout(
		{
			name,
			path,
			exact: true,
			component: ViewCustomPage,
			resources: __SERVER__ ? viewCustomPageSSRResources : [ViewCustomPageResource],
		},
		ScreenName.VIEW_CUSTOM_PAGE,
		{
			paperAppearance: 'none',
			fadeInSearchModal: true,
			hideSearchButton: true,
			hideBanner: true,
			removeHeader: true,
			prefetchBrandingModel: true,
			hideSkipToMainContent: true,
		},
	);
};

interface Path {
	location: {
		pathname: string;
	};
}

export const EditCustomPageLayout = (props: Path) => {
	const { formatMessage } = useIntl();
	const WrappedComponent = compose(
		withOnlyLoggedIn({
			redirectUrl: props.location.pathname,
		}),
		withOnlyJiraAdmin({
			message: formatMessage(authPageMessage),
			callToActionText: formatMessage(callToActionUrlText),
			callToActionUrl: '/',
		}),
	)(() => (
		<SplashLayoutRoute
			path={RoutePath.EDIT_CUSTOM_PAGE}
			component={EditCustomPage}
			screenName={ScreenName.EDIT_CUSTOM_PAGE}
			isFooterHidden={isIncreasedFlexibilityEnabled()}
		/>
	));

	return (
		<>
			<PrefetchBrandingModel />
			<WrappedComponent {...props} />
		</>
	);
};

export const getEditCustomPageRoute = (
	name: string = RouteName.EDIT_CUSTOM_PAGE,
	path: string = RoutePath.EDIT_CUSTOM_PAGE,
) => {
	const editHomePageRoute: Route = {
		name,
		path,
		exact: true,
		component: React.memo(EditCustomPageLayout),
		resources: [EditCustomPageResource],
	};
	return editHomePageRoute;
};
